/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import { ThemeProvider } from '@atlaskit/app-provider/theme-provider';
import { useSetColorMode } from '@atlaskit/app-provider/use-set-color-mode';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ExampleBackground from './background';
import type { Background, ColorMode } from './types';

const styles = cssMap({
	background: {
		borderStartStartRadius: token('radius.small'),
		borderStartEndRadius: token('radius.small'),
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	exampleContent: {
		color: token('color.text'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.150'),
	},
});

type ExampleShowcaseProps = {
	backgroundColor?: Background;
	children: React.ReactNode;
	colorMode?: ColorMode;
	testId?: string;
};

/**
 * __Example showcase__
 *
 * An example showcase displays an example with code and link to codesandbox.
 *
 */
const ExampleShowcase: ({
	backgroundColor,
	children,
	colorMode,
	testId,
}: ExampleShowcaseProps) => JSX.Element = ({
	backgroundColor = 'checkered',
	children,
	colorMode,
	testId,
}: ExampleShowcaseProps) => {
	return (
		<ThemeProvider defaultColorMode={colorMode}>
			<ExampleShowcaseContent
				backgroundColor={backgroundColor}
				colorMode={colorMode}
				testId={testId}
			>
				{children}
			</ExampleShowcaseContent>
		</ThemeProvider>
	);
};

type ExampleShowcaseContentProps = {
	backgroundColor?: Background;
	children: React.ReactNode;
	colorMode?: ColorMode;
	testId?: string;
};

const ExampleShowcaseContent = ({
	backgroundColor = 'checkered',
	children,
	colorMode,
	testId,
}: ExampleShowcaseContentProps) => {
	const setColorMode = useSetColorMode();
	useEffect(() => {
		setColorMode(colorMode || 'auto');
	}, [colorMode, setColorMode]);

	return (
		<ExampleBackground background={backgroundColor} xcss={styles.background} testId={testId}>
			<Box xcss={styles.exampleContent}>{children}</Box>
		</ExampleBackground>
	);
};

export default ExampleShowcase;
